import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Plus } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { apiClient } from '@/lib/api-client';
import type { TaskPriority } from '@/api/generated/client/models';
import type { AssignableUser } from '@/api/generated/client/models';

const taskFormSchema = z.object({
  claimId: z.number().min(1, 'Claim ID is required'),
  title: z.string().min(1, 'Title is required').max(255, 'Title is too long'),
  description: z.string().optional(),
  priority: z.enum(['low', 'medium', 'high', 'urgent'] as const),
  dueDate: z.string().optional(),
  assignedTo: z.number().optional(),
});

type TaskFormValues = z.infer<typeof taskFormSchema>;

interface CreateTaskDialogProps {
  onCreateTask: (task: { claimId: number; title: string; description?: string; priority: TaskPriority; dueDate?: Date; assignedTo?: number }) => void;
  defaultClaimId?: number;
  isCreating?: boolean;
}

export function CreateTaskDialog({ onCreateTask, defaultClaimId, isCreating }: CreateTaskDialogProps) {
  const [open, setOpen] = useState(false);

  // Fetch all claims for the dropdown
  const { data: claimsResponse } = useQuery({
    queryKey: ['claims'],
    queryFn: async () => {
      return await apiClient.claims.listClaims({ page: 1, limit: 100 });
    },
  });

  const claims = claimsResponse?.claims || [];
  const writableClaims = claims.filter((claim) => claim.canWrite ?? false);

  const form = useForm<TaskFormValues>({
    resolver: zodResolver(taskFormSchema) as unknown as any,
    defaultValues: {
      claimId: defaultClaimId || 0,
      title: '',
      description: '',
      priority: 'medium',
      dueDate: '',
      assignedTo: undefined,
    },
  });

  const selectedClaimId = form.watch("claimId");

  // Fetch assignable users for the selected claim
  // Note: Unlike CreateTaskModal/UpdateTaskModal which use useTasks(claimId),
  // this dialog allows dynamic claim selection, so we query inline with conditional enabling
  const { data: assignableUsersData } = useQuery({
    queryKey: ["task-assignees", selectedClaimId],
    queryFn: async () => {
      const res = await apiClient.tasks.getTaskAssignees({ claimId: selectedClaimId });
      return res.data ?? [];
    },
    enabled: open && !!selectedClaimId && selectedClaimId > 0,
  });

  const assignableUsers: AssignableUser[] = assignableUsersData ?? [];

  const onSubmit = (values: TaskFormValues) => {
    const canWrite = writableClaims.some((claim) => claim.id === values.claimId);
    if (!canWrite) {
      form.setError('claimId', {
        type: 'manual',
        message: 'You can only create tasks for claims you have permission to edit.',
      });
      return;
    }

    // Parse date string to create a Date at noon local time to avoid timezone issues
    let dueDate: Date | undefined;
    if (values.dueDate) {
      const [year, month, day] = values.dueDate.split('-').map(Number);
      dueDate = new Date(year, month - 1, day, 12, 0, 0);
    }

    onCreateTask({
      claimId: values.claimId,
      title: values.title,
      description: values.description || undefined,
      priority: values.priority as TaskPriority,
      dueDate,
      assignedTo: values.assignedTo,
    });
    setOpen(false);
    form.reset();
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {/* Only disable once claims have loaded and there are genuinely no editable
            claims — avoids disabling the trigger during the async claims fetch. */}
        <Button className="w-full md:w-auto" disabled={claimsResponse !== undefined && writableClaims.length === 0}>
          <Plus className="h-4 w-4 mr-2" />
          Create Task
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Create New Task</DialogTitle>
          <DialogDescription>
            Add a new task to track work on a claim.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={form.handleSubmit(onSubmit) as unknown as any} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="claimId">Claim *</Label>
            <Select
              onValueChange={(val) => form.setValue("claimId", parseInt(val))}
              defaultValue={defaultClaimId ? defaultClaimId.toString() : undefined}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a claim" />
              </SelectTrigger>
              <SelectContent>
                {writableClaims.map((claim) => (
                  <SelectItem key={claim.id} value={claim.id.toString()}>
                    #{claim.claimNumber} - {claim.insuredName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {writableClaims.length === 0 && (
              <p className="text-xs text-muted-foreground">No editable claims available. Tasks can only be created for claims with write access.</p>
            )}
            {form.formState.errors.claimId && (
              <p className="text-xs text-destructive">{form.formState.errors.claimId.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="title">Title *</Label>
            <Input
              id="title"
              placeholder="Enter task title"
              {...form.register("title")}
            />
            {form.formState.errors.title && (
              <p className="text-xs text-destructive">{form.formState.errors.title.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              placeholder="Enter task description"
              className="resize-none"
              rows={3}
              {...form.register("description")}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="priority">Priority *</Label>
              <Select
                onValueChange={(val) => form.setValue("priority", val as 'low' | 'medium' | 'high' | 'urgent')}
                defaultValue={form.getValues("priority")}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="dueDate">Due Date</Label>
              <Input
                id="dueDate"
                type="date"
                {...form.register("dueDate")}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="assignedTo">Assignee</Label>
            <Select
              onValueChange={(val) => form.setValue("assignedTo", val === "unassigned" ? undefined : parseInt(val))}
              value={form.watch("assignedTo") ? String(form.watch("assignedTo")) : "unassigned"}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select assignee" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="unassigned">Unassigned</SelectItem>
                {assignableUsers.map((u) => (
                  <SelectItem key={u.id} value={String(u.id)}>
                    {u.name || u.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isCreating}>
              {isCreating ? 'Creating...' : 'Create Task'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
